import type { ActivityType } from "@/entities/Activity/types/activityType";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	Tooltip,
	type TooltipProps,
	XAxis,
	YAxis,
} from "recharts";
import type {
	NameType,
	ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import styles from "./ActivityGraph.module.scss";

type Props = {
	activities: {
		completionCount: number;
		completedAt: string;
		activity: ActivityType;
	}[];
};

const CustomTooltip = ({
	active,
	payload,
	label,
}: TooltipProps<ValueType, NameType>) => {
	if (active && payload && payload.length) {
		const data = payload[0]?.payload;
		return (
			<div className={styles.customTooltip}>
				<p className="label">{`${label} : ${payload[0].value}`}</p>
				<p>{`Activity: ${data?.activity}`}</p>
				<p>{`Attempts: ${data?.attempts}`}</p>
			</div>
		);
	}

	return null;
};

const ActivityGraph: React.FC<Props> = ({ activities }) => {
	const graphContainerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);

	useEffect(() => {
		const handleResize = () => {
			if (graphContainerRef.current) {
				setContainerWidth(graphContainerRef.current.offsetWidth);
			}
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const data = activities?.map((activity) => {
		return {
			date: new Date(activity?.completedAt)?.toLocaleDateString(),
			activity: activity?.activity?.title,
			attempts: activity?.completionCount,
		};
	});

	return (
		<div className={styles.graphContainer} ref={graphContainerRef}>
			<h2>Activity Graph</h2>
			{containerWidth && (
				<LineChart width={containerWidth} height={600} data={data}>
					<XAxis dataKey="date" />
					<YAxis />
					<CartesianGrid strokeDasharray="3 3" />
					<Tooltip content={<CustomTooltip />} />
					<Legend />
					<Line
						type="monotone"
						dataKey="attempts"
						stroke="#8884d8"
						activeDot={{ r: 8 }}
					/>
				</LineChart>
			)}
		</div>
	);
};

export default ActivityGraph;
